import React from "react";
import { Mail, Phone, Instagram, Youtube, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface ContactInformationProps {
  influencer: {
    id: string;
    realName: string;
    handle: string;
    platforms: string[];
    location: string;
    _db?: {
      email?: string | null;
      phone?: string | null;
      location?: string | null;
      handle: string;
      platform: string;
    } | null;
  };
}

export default function ContactInformation({ influencer }: ContactInformationProps) {
  const navigate = useNavigate();
  const email = influencer._db?.email;
  const rawHandle = influencer._db?.handle || influencer.handle || "";
  const handle = rawHandle.replace("@", "");
  const platform = influencer._db?.platform || influencer.platforms[0]?.toLowerCase();

  const handleEmailClick = () => {
    navigate(`/email-center?influencerId=${influencer.id}`);
  };

  const getProfileUrl = () => {
    if (platform === "instagram") {
      return `https://instagram.com/${handle}`;
    } else if (platform === "youtube") {
      return `https://youtube.com/@${handle}`;
    }
    return null;
  };

  const profileUrl = getProfileUrl();

  return (
    <div className="w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-6">
      {/* Header section */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#F9F5FF] text-[#A655F8] flex items-center justify-center">
          <Phone className="w-5 h-5" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">Contact Information</h2>
      </div>

      {/* Influencer Name & Email */}
      <div className="space-y-3">
        <h3 className="font-bold text-[18px] text-slate-900 leading-none">{influencer.realName}</h3>
        
        {email ? (
          <div className="flex items-center justify-between p-3 bg-[#F8F9FF] border border-slate-100 rounded-xl gap-4">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Mail className="w-5 h-5 text-slate-400 shrink-0" />
              <span className="text-sm font-medium text-slate-600 truncate">{email}</span>
            </div>
            <Button
              size="sm"
              onClick={handleEmailClick}
              className="bg-[#F9F5FF] hover:bg-[#F3E8FF] text-[#A655F8] border border-[#F3E8FF] font-semibold text-xs py-1.5 px-3 rounded-lg transition-colors shrink-0 shadow-none"
            >
              Send Email
            </Button>
          </div>
        ) : (
          <div className="text-sm text-slate-400 italic">No email address available</div>
        )}
      </div>

      {/* Social Handles */}
      <div className="space-y-3">
        <h4 className="font-bold text-[16px] text-slate-900">Social Handles</h4>
        {profileUrl ? (
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors group cursor-pointer"
          >
            <div className="flex items-center gap-2">
              {platform === "instagram" ? (
                <img src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-5 h-5 object-contain" />
              ) : (
                <Youtube className="w-5 h-5 text-slate-400 group-hover:text-red-500 transition-colors" />
              )}
              <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
                @{handle}
              </span>
            </div>
            <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </a>
        ) : (
          <div className="text-sm text-slate-400 italic">No social handles available</div>
        )}
      </div>
    </div>
  );
}
